import { Dispatch, useState } from 'react';

import { requestService } from 'shared/utils/services/requestService';
import { IAction, IErrorAction, ITabChangeAction } from 'checkout/types/ActionPayload';
import { IPartialOrder } from 'checkout/types/Order';
import { IAddress } from 'checkout/types/Address';
import { actions } from 'checkout/state/checkoutActions';
import { ITabName } from 'checkout/types/Tab';

import { usePayments } from './usePayments';

interface IUseBraintree {
  dispatch: Dispatch<IAction | IErrorAction | ITabChangeAction>;
  order?: IPartialOrder;
  addresses: { [id: number]: IAddress };
}

// we can't use the type imported from the lib bc of the server render issue below
export interface IBraintreePaypalInstance {
  teardown: () => void;
  paypal: {
    initAuthFlow: () => void;
    closeAuthFlow: () => void;
  };
}

/**
 * This uses the v2 Braintree integration instead of the newest v3 because I couldn't get the
 * v3 PayPal nonce to work with our existing backend.
 *
 * There's 3 different types of payment options with PayPal: vault, checkout, checkout with vault.
 * Vault allows us to authorize PayPal and use it for later purchases. Checkout executes a one-time
 * payment. Checkout with vault executes a one-time payment and allows us to charge future purchases too.
 * I believe our backend is set up to only work with vault which I can only find v2 docs for.
 *
 * We also have a very unique checkout flow in that we want our own button that opens the PayPal dialog
 * and only finalize the purchase after the user reaches the Review page and confirms the purchase.
 * This is not a part of the standard PayPal flow which is why we use the 'custom' setup in Braintree.
 * Again, I couldn't find a direct v3 equivalent of this to upgrade to so here we are.
 *
 * Most of the params here are copied from old goat.com.
 *
 * As you can probably tell, this implementation is very jank. If you're reading this and you're confused,
 * don't worry I am too. The official v2 docs are hard to find and somewhat incomplete so it's
 * probably time to upgrade to v3. Backend will need to upgrade to v3 as well.
 */
export const useBraintree = ({ dispatch, order, addresses }: IUseBraintree) => {
  const { createPayPalBillingInfo, connectBillingInfoToOrder } = usePayments({ dispatch, order });
  const [braintreeClient, setBraintreeClient] = useState<IBraintreePaypalInstance>(null);
  const [braintreeLibrary, setBraintreeLibrary] = useState(null);
  const [isBraintreeLoading, setIsBraintreeLoading] = useState(false);
  let oneTimeNonce = '';

  const initBraintree = async (paymentReceivedCallback: () => void) => {
    if (braintreeClient || braintreeLibrary || isBraintreeLoading) {
      return;
    }

    const paypalShippingAddress = addresses[order.addressId];

    const { clientTokenPaypal } = await requestService({
      url: '/web-api/v1/users/braintree_client_token',
      method: 'get',
    });

    // this version of the SDK uses window.navigator onload which isn't available on server render
    // so we need to defer loading of this SDK to run client-side only
    setIsBraintreeLoading(true);
    const Braintree = await import('braintree-web');
    setBraintreeLibrary(Braintree);

    Braintree.setup(clientTokenPaypal, 'custom', {
      paypal: {
        headless: true, // tells the API we don't want the default PayPal button
        enableShippingAddress: true,
        shippingAddressOverride: {
          recipientName: paypalShippingAddress.name,
          streetAddress: `${paypalShippingAddress.address1} ${paypalShippingAddress.address2}`,
          city: paypalShippingAddress.city,
          countryCode: paypalShippingAddress.countryCode,
          postalCode: paypalShippingAddress.postalCode,
          state: paypalShippingAddress.stateCode,
          phone: paypalShippingAddress.phone,
          locality: paypalShippingAddress.city,
          countryCodeAlpha2: paypalShippingAddress.countryCode,
          region: paypalShippingAddress.stateCode,
        },
      },
      onReady: (integration) => {
        setBraintreeClient(integration);
        setIsBraintreeLoading(false);
      },
      /**
       * NOTE: for braintree v2 onPaymentMethodReceived function gets called
       * multiple times in the paypal lifecycle. since we won't be upgrading to
       * v3 will check if the one time use nonce has already been generated and
       * if so will ignore subsequent calls
       */
      onPaymentMethodReceived: async (payload) => {
        // set loading status before server calls
        paymentReceivedCallback();

        if (oneTimeNonce) return;

        oneTimeNonce = payload?.nonce;
        // Submit `payload.nonce` to your server
        const paypalBillingInfo = await createPayPalBillingInfo({
          nonce: payload?.nonce,
          email: payload?.details.email,
        });

        await connectBillingInfoToOrder(paypalBillingInfo.id);

        dispatch({ type: actions.changeTab, payload: ITabName.REVIEW });
      },
    });
  };

  return {
    braintreeClient,
    initBraintree,
  };
};
